import {
  StayAmenity,
  StayCancellationPolicy,
  StayCheckInMethod,
  StayListingStatus,
  StayPropertyType,
  StayRoomType,
  type IUpdateStayInput,
} from '../../../__generated__/graphql';
import type { BadgeTone } from '../../components/ui';
import type { IStayHostListingFieldsFragment } from '../../services/stayService/__generated__/queries.generated';

/**
 * The host's stay form (spec 016 W6 to W8). One flat object of editable values
 * shared by the listing wizard (one step at a time) and the manage page (one
 * section at a time). Numbers are kept as the text the host typed, so an empty
 * box stays empty; `toUpdateInput` parses them and checks the same limits as
 * the API (`UpdateStayInput` in the backend).
 */
export type StayListing = IStayHostListingFieldsFragment;

export interface StayPrivateValues {
  checkInInstructions?: string | null;
  wifiName?: string | null;
  wifiPassword?: string | null;
}

export interface StayForm {
  name: string;
  description: string;
  descriptionSpace: string;
  descriptionGuestAccess: string;
  descriptionNeighborhood: string;
  propertyType: StayPropertyType;
  roomType: StayRoomType;
  maxGuests: string;
  bedrooms: string;
  beds: string;
  bathrooms: string;
  amenities: StayAmenity[];
  petsAllowed: boolean;
  maxPets: string;
  smokingAllowed: boolean;
  eventsAllowed: boolean;
  childrenAllowed: boolean;
  infantsAllowed: boolean;
  quietHoursFrom: string;
  quietHoursTo: string;
  additionalRules: string;
  checkInFrom: string;
  checkInUntil: string;
  checkOutBy: string;
  selfCheckIn: boolean;
  checkInMethod: StayCheckInMethod | '';
  checkInInstructions: string;
  wifiName: string;
  wifiPassword: string;
  instantBook: boolean;
  requireVerifiedId: boolean;
  weekdayPrice: string;
  weekendPrice: string;
  cleaningFee: string;
  extraGuestFee: string;
  extraGuestAfter: string;
  petFee: string;
  weeklyDiscountPercent: string;
  monthlyDiscountPercent: string;
  nonRefundableDiscountPercent: string;
  cancellationPolicy: StayCancellationPolicy;
  minimumNights: string;
  maximumNights: string;
  advanceNoticeHours: string;
  preparationDays: string;
}

export type StayFormKey = keyof StayForm;

// ─── labels ────────────────────────────────────────────────────────────────

export const PROPERTY_TYPE_LABELS: Record<StayPropertyType, string> = {
  [StayPropertyType.House]: 'House',
  [StayPropertyType.Apartment]: 'Apartment',
  [StayPropertyType.Guesthouse]: 'Guesthouse',
  [StayPropertyType.Hotel]: 'Hotel',
  [StayPropertyType.Cabin]: 'Cabin',
  [StayPropertyType.Villa]: 'Villa',
  [StayPropertyType.Condo]: 'Condo',
  [StayPropertyType.Townhouse]: 'Townhouse',
  [StayPropertyType.TinyHome]: 'Tiny home',
  [StayPropertyType.Other]: 'Other',
};

export const ROOM_TYPE_LABELS: Record<StayRoomType, { title: string; description: string }> = {
  [StayRoomType.EntirePlace]: {
    title: 'An entire place',
    description: 'Guests have the whole place to themselves.',
  },
  [StayRoomType.PrivateRoom]: {
    title: 'A room',
    description: 'Guests have their own room and share common areas.',
  },
  [StayRoomType.SharedRoom]: {
    title: 'A shared room',
    description: 'Guests sleep in a room or common area they may share with others.',
  },
};

export const AMENITY_GROUPS: { title: string; items: StayAmenity[] }[] = [
  {
    title: 'Guest favorites',
    items: [
      StayAmenity.Wifi,
      StayAmenity.Tv,
      StayAmenity.Kitchen,
      StayAmenity.Washer,
      StayAmenity.Dryer,
      StayAmenity.AirConditioning,
      StayAmenity.Heating,
      StayAmenity.DedicatedWorkspace,
      StayAmenity.FreeParking,
      StayAmenity.PaidParking,
      StayAmenity.EvCharger,
    ],
  },
  {
    title: 'Standout amenities',
    items: [
      StayAmenity.Pool,
      StayAmenity.HotTub,
      StayAmenity.Patio,
      StayAmenity.BbqGrill,
      StayAmenity.Gym,
      StayAmenity.Breakfast,
      StayAmenity.LakeAccess,
      StayAmenity.BeachAccess,
      StayAmenity.SkiInOut,
    ],
  },
  {
    title: 'Comfort and family',
    items: [
      StayAmenity.Essentials,
      StayAmenity.Hangers,
      StayAmenity.Iron,
      StayAmenity.HairDryer,
      StayAmenity.Crib,
      StayAmenity.HighChair,
    ],
  },
  {
    title: 'Safety',
    items: [
      StayAmenity.SmokeAlarm,
      StayAmenity.CarbonMonoxideAlarm,
      StayAmenity.FirstAidKit,
      StayAmenity.FireExtinguisher,
    ],
  },
];

/** "AIR_CONDITIONING" → "Air conditioning", with a few names spelled out. */
export function amenityLabel(amenity: StayAmenity): string {
  const special: Partial<Record<StayAmenity, string>> = {
    [StayAmenity.Wifi]: 'Wi-Fi',
    [StayAmenity.Tv]: 'TV',
    [StayAmenity.EvCharger]: 'EV charger',
    [StayAmenity.BbqGrill]: 'BBQ grill',
    [StayAmenity.SkiInOut]: 'Ski-in, ski-out',
    [StayAmenity.CarbonMonoxideAlarm]: 'Carbon monoxide alarm',
  };
  if (special[amenity]) return special[amenity] as string;
  const words = amenity.toLowerCase().split('_');
  return [words[0].charAt(0).toUpperCase() + words[0].slice(1), ...words.slice(1)].join(' ');
}

export const CANCELLATION_POLICY_LABELS: Record<
  StayCancellationPolicy,
  { title: string; description: string }
> = {
  [StayCancellationPolicy.Flexible]: {
    title: 'Flexible',
    description: 'Full refund up to 1 day before check-in.',
  },
  [StayCancellationPolicy.Moderate]: {
    title: 'Moderate',
    description: 'Full refund up to 5 days before check-in.',
  },
  [StayCancellationPolicy.Firm]: {
    title: 'Firm',
    description:
      'Full refund up to 30 days before check-in. Half refund up to 7 days before check-in.',
  },
  [StayCancellationPolicy.Strict]: {
    title: 'Strict',
    description:
      'Full refund for 48 hours after booking if check-in is 14 or more days away. Half refund up to 7 days before check-in.',
  },
  [StayCancellationPolicy.NonRefundable]: {
    title: 'Non-refundable',
    description: 'No refund after booking. Guests get a discount of at least 10% for it.',
  },
};

export const CHECK_IN_METHOD_LABELS: Record<StayCheckInMethod, string> = {
  [StayCheckInMethod.SmartLock]: 'Smart lock',
  [StayCheckInMethod.Keypad]: 'Keypad',
  [StayCheckInMethod.Lockbox]: 'Lockbox',
  [StayCheckInMethod.HostGreets]: 'I greet guests in person',
  [StayCheckInMethod.BuildingStaff]: 'Building staff',
};

export const LISTING_STATUS_LABELS: Record<StayListingStatus, { label: string; tone: BadgeTone }> =
  {
    [StayListingStatus.Draft]: { label: 'In progress', tone: 'warning' },
    [StayListingStatus.InProgress]: { label: 'Ready to publish', tone: 'info' },
    [StayListingStatus.Listed]: { label: 'Listed', tone: 'success' },
    [StayListingStatus.Unlisted]: { label: 'Unlisted', tone: 'neutral' },
    [StayListingStatus.Snoozed]: { label: 'Snoozed', tone: 'neutral' },
  };

/** A draft goes back to the wizard; a published place opens the manage page. */
export function isDraftStatus(status?: StayListingStatus | null): boolean {
  return !status || status === StayListingStatus.Draft || status === StayListingStatus.InProgress;
}

/** "Cabin in Boulder, CO". */
export function listingSummary(listing: Pick<StayListing, 'stayDetails' | 'location'>): string {
  const type = listing.stayDetails?.propertyType
    ? PROPERTY_TYPE_LABELS[listing.stayDetails.propertyType]
    : 'Place';
  const where = [listing.location?.city, listing.location?.state].filter(Boolean).join(', ');
  return where ? `${type} in ${where}` : type;
}

// ─── form ↔ listing ────────────────────────────────────────────────────────

const text = (value?: string | null) => value ?? '';
const num = (value?: number | null) =>
  value === null || value === undefined || Number.isNaN(value) ? '' : String(value);

export function emptyStayForm(): StayForm {
  return {
    name: '',
    description: '',
    descriptionSpace: '',
    descriptionGuestAccess: '',
    descriptionNeighborhood: '',
    propertyType: StayPropertyType.House,
    roomType: StayRoomType.EntirePlace,
    maxGuests: '4',
    bedrooms: '1',
    beds: '1',
    bathrooms: '1',
    amenities: [],
    petsAllowed: false,
    maxPets: '0',
    smokingAllowed: false,
    eventsAllowed: false,
    childrenAllowed: true,
    infantsAllowed: true,
    quietHoursFrom: '',
    quietHoursTo: '',
    additionalRules: '',
    checkInFrom: '15:00',
    checkInUntil: '',
    checkOutBy: '11:00',
    selfCheckIn: false,
    checkInMethod: '',
    checkInInstructions: '',
    wifiName: '',
    wifiPassword: '',
    instantBook: true,
    requireVerifiedId: false,
    weekdayPrice: '',
    weekendPrice: '',
    cleaningFee: '0',
    extraGuestFee: '0',
    extraGuestAfter: '',
    petFee: '0',
    weeklyDiscountPercent: '0',
    monthlyDiscountPercent: '0',
    nonRefundableDiscountPercent: '',
    cancellationPolicy: StayCancellationPolicy.Moderate,
    minimumNights: '1',
    maximumNights: '365',
    advanceNoticeHours: '24',
    preparationDays: '0',
  };
}

export function stayToForm(
  listing: StayListing,
  privateValues?: StayPrivateValues | null,
): StayForm {
  const stay = listing.stayDetails;
  const rules = stay?.houseRules;
  const base = emptyStayForm();
  return {
    ...base,
    name: text(listing.name),
    description: text(listing.description),
    descriptionSpace: text(stay?.descriptionSpace),
    descriptionGuestAccess: text(stay?.descriptionGuestAccess),
    descriptionNeighborhood: text(stay?.descriptionNeighborhood),
    propertyType: stay?.propertyType ?? base.propertyType,
    roomType: stay?.roomType ?? base.roomType,
    maxGuests: num(stay?.maxGuests),
    bedrooms: num(stay?.bedrooms),
    beds: num(stay?.beds),
    bathrooms: num(stay?.bathrooms),
    amenities: [...(stay?.amenities ?? [])],
    petsAllowed: rules?.petsAllowed ?? base.petsAllowed,
    maxPets: num(rules?.maxPets ?? 0),
    smokingAllowed: rules?.smokingAllowed ?? base.smokingAllowed,
    eventsAllowed: rules?.eventsAllowed ?? base.eventsAllowed,
    childrenAllowed: rules?.childrenAllowed ?? base.childrenAllowed,
    infantsAllowed: rules?.infantsAllowed ?? base.infantsAllowed,
    quietHoursFrom: text(rules?.quietHoursFrom),
    quietHoursTo: text(rules?.quietHoursTo),
    additionalRules: text(rules?.additionalRules),
    checkInFrom: stay?.checkInFrom ?? base.checkInFrom,
    checkInUntil: text(stay?.checkInUntil),
    checkOutBy: stay?.checkOutBy ?? base.checkOutBy,
    selfCheckIn: stay?.selfCheckIn ?? base.selfCheckIn,
    checkInMethod: stay?.checkInMethod ?? '',
    checkInInstructions: text(privateValues?.checkInInstructions),
    wifiName: text(privateValues?.wifiName),
    wifiPassword: text(privateValues?.wifiPassword),
    instantBook: stay?.instantBook ?? base.instantBook,
    requireVerifiedId: stay?.requireVerifiedId ?? base.requireVerifiedId,
    weekdayPrice: num(listing.weekdayPrice),
    weekendPrice: num(listing.weekendPrice),
    cleaningFee: num(stay?.cleaningFee ?? 0),
    extraGuestFee: num(stay?.extraGuestFee ?? 0),
    extraGuestAfter: num(stay?.extraGuestAfter),
    petFee: num(stay?.petFee ?? 0),
    weeklyDiscountPercent: num(listing.discount?.oneWeek ?? 0),
    monthlyDiscountPercent: num(listing.discount?.oneMonth ?? 0),
    nonRefundableDiscountPercent: num(listing.discount?.nonRefundableDiscount),
    cancellationPolicy: stay?.cancellationPolicy ?? base.cancellationPolicy,
    minimumNights: num(listing.minimumRentalDays ?? 1),
    maximumNights: num(listing.maximumRentalDays ?? 365),
    advanceNoticeHours: num(listing.tripPreferences?.advanceNoticeHours ?? 24),
    preparationDays: num(stay?.preparationDays ?? 0),
  };
}

// ─── form → UpdateStayInput ────────────────────────────────────────────────

const HH_MM = /^([01]\d|2[0-3]):[0-5]\d$/;

interface NumberRule {
  min: number;
  max: number;
  integer?: boolean;
  /** Empty sends null (clears the value) instead of an error. */
  optional?: boolean;
  label: string;
}

const NUMBER_RULES: Partial<Record<StayFormKey, NumberRule>> = {
  maxGuests: { min: 1, max: 16, integer: true, label: 'Guests' },
  bedrooms: { min: 0, max: 50, integer: true, label: 'Bedrooms' },
  beds: { min: 1, max: 50, integer: true, label: 'Beds' },
  bathrooms: { min: 0, max: 50, label: 'Bathrooms' },
  maxPets: { min: 0, max: 10, integer: true, label: 'Pets' },
  weekdayPrice: { min: 10, max: 25000, label: 'Nightly price' },
  weekendPrice: { min: 10, max: 25000, optional: true, label: 'Weekend price' },
  cleaningFee: { min: 0, max: 5000, label: 'Cleaning fee' },
  extraGuestFee: { min: 0, max: 1000, label: 'Extra guest fee' },
  extraGuestAfter: { min: 1, max: 16, integer: true, optional: true, label: 'Extra guests after' },
  petFee: { min: 0, max: 1000, label: 'Pet fee' },
  weeklyDiscountPercent: { min: 0, max: 99, label: 'Weekly discount' },
  monthlyDiscountPercent: { min: 0, max: 99, label: 'Monthly discount' },
  nonRefundableDiscountPercent: {
    min: 10,
    max: 50,
    optional: true,
    label: 'Non-refundable discount',
  },
  minimumNights: { min: 1, max: 365, integer: true, label: 'Minimum nights' },
  maximumNights: { min: 1, max: 365, integer: true, label: 'Maximum nights' },
  advanceNoticeHours: { min: 0, max: 168, integer: true, label: 'Advance notice' },
  preparationDays: { min: 0, max: 2, integer: true, label: 'Preparation time' },
};

const HOUSE_RULE_KEYS = [
  'petsAllowed',
  'maxPets',
  'smokingAllowed',
  'eventsAllowed',
  'childrenAllowed',
  'infantsAllowed',
  'quietHoursFrom',
  'quietHoursTo',
  'additionalRules',
] as const;

const TIME_KEYS: StayFormKey[] = ['checkInFrom', 'checkOutBy', 'checkInUntil'];
const OPTIONAL_TIME_KEYS: StayFormKey[] = ['checkInUntil', 'quietHoursFrom', 'quietHoursTo'];

export interface UpdateInputResult {
  input: IUpdateStayInput;
  /** Field key → message. Empty when the values can be sent. */
  errors: Partial<Record<StayFormKey, string>>;
}

/**
 * The `UpdateStayInput` for the given fields of the form, with the API's
 * limits checked first so the host sees the problem next to the box.
 */
export function toUpdateInput(form: StayForm, keys: readonly StayFormKey[]): UpdateInputResult {
  const input: Record<string, unknown> = {};
  const errors: Partial<Record<StayFormKey, string>> = {};
  const houseRules: Record<string, unknown> = {};

  for (const key of keys) {
    const value = form[key];
    const rule = NUMBER_RULES[key];
    let parsed: unknown = value;

    if (rule) {
      const raw = String(value).trim();
      if (raw === '') {
        if (rule.optional) {
          parsed = null;
        } else {
          errors[key] = `${rule.label} is required`;
          continue;
        }
      } else {
        const n = Number(raw);
        if (!Number.isFinite(n) || (rule.integer && !Number.isInteger(n))) {
          errors[key] = `${rule.label} must be a ${rule.integer ? 'whole ' : ''}number`;
          continue;
        }
        if (n < rule.min || n > rule.max) {
          errors[key] = `${rule.label} must be between ${rule.min} and ${rule.max}`;
          continue;
        }
        parsed = n;
      }
    } else if (TIME_KEYS.includes(key) || OPTIONAL_TIME_KEYS.includes(key)) {
      const raw = String(value).trim();
      if (raw === '') {
        if (OPTIONAL_TIME_KEYS.includes(key)) {
          parsed = null;
        } else {
          errors[key] = 'Pick a time';
          continue;
        }
      } else if (!HH_MM.test(raw)) {
        errors[key] = 'Use a time like 15:00';
        continue;
      } else {
        parsed = raw;
      }
    } else if (key === 'checkInMethod') {
      parsed = value === '' ? null : value;
    } else if (typeof value === 'string') {
      parsed = value.trim();
      if (key === 'name') {
        if (!parsed) {
          errors.name = 'Add a title';
          continue;
        }
        if ((parsed as string).length > 50) {
          errors.name = 'Keep the title to 50 characters';
          continue;
        }
      }
      if (['checkInInstructions', 'wifiName', 'wifiPassword'].includes(key) && parsed === '') {
        parsed = null;
      }
    }

    if ((HOUSE_RULE_KEYS as readonly string[]).includes(key)) {
      houseRules[key] = parsed;
    } else {
      input[key] = parsed;
    }
  }

  if (Object.keys(houseRules).length) {
    if (houseRules.petsAllowed === false) houseRules.maxPets = 0;
    input.houseRules = houseRules;
  }

  const min = input.minimumNights as number | undefined;
  const max = input.maximumNights as number | undefined;
  if (min !== undefined && max !== undefined && min > max) {
    errors.maximumNights = 'The maximum stay must be at least the minimum stay';
  }
  if (
    input.cancellationPolicy === StayCancellationPolicy.NonRefundable &&
    keys.includes('nonRefundableDiscountPercent') &&
    input.nonRefundableDiscountPercent === null
  ) {
    // The API starts a non-refundable policy at 10%; say so instead of sending null.
    input.nonRefundableDiscountPercent = 10;
  }

  return { input: input as IUpdateStayInput, errors };
}

// ─── sections and wizard steps ─────────────────────────────────────────────

export type StaySectionId =
  | 'basics'
  | 'amenities'
  | 'title'
  | 'description'
  | 'booking'
  | 'houseRules'
  | 'price'
  | 'checkIn'
  | 'cancellation';

/** The form fields each section saves. The wizard and the manage page share them. */
export const SECTION_FIELDS: Record<StaySectionId, readonly StayFormKey[]> = {
  basics: ['propertyType', 'roomType', 'maxGuests', 'bedrooms', 'beds', 'bathrooms'],
  amenities: ['amenities'],
  title: ['name'],
  description: [
    'description',
    'descriptionSpace',
    'descriptionGuestAccess',
    'descriptionNeighborhood',
  ],
  booking: [
    'instantBook',
    'requireVerifiedId',
    'minimumNights',
    'maximumNights',
    'advanceNoticeHours',
    'preparationDays',
  ],
  houseRules: [
    'petsAllowed',
    'maxPets',
    'smokingAllowed',
    'eventsAllowed',
    'childrenAllowed',
    'infantsAllowed',
    'quietHoursFrom',
    'quietHoursTo',
    'additionalRules',
  ],
  price: [
    'weekdayPrice',
    'weekendPrice',
    'cleaningFee',
    'extraGuestFee',
    'extraGuestAfter',
    'petFee',
    'weeklyDiscountPercent',
    'monthlyDiscountPercent',
  ],
  checkIn: [
    'checkInFrom',
    'checkInUntil',
    'checkOutBy',
    'selfCheckIn',
    'checkInMethod',
    'checkInInstructions',
    'wifiName',
    'wifiPassword',
  ],
  cancellation: ['cancellationPolicy', 'nonRefundableDiscountPercent'],
};

export type WizardStepId =
  | 'property-type'
  | 'room-type'
  | 'location'
  | 'basics'
  | 'amenities'
  | 'photos'
  | 'title'
  | 'description'
  | 'booking'
  | 'house-rules'
  | 'price'
  | 'check-in'
  | 'cancellation'
  | 'review';

export interface WizardStep {
  id: WizardStepId;
  title: string;
  description: string;
  /** Fields saved with `updateStay` on Next; absent for steps with their own save. */
  section?: StaySectionId;
}

/** The 14 wizard steps (spec 016 §4.10.3). `wizardStep` on the stay stores the id reached. */
export const WIZARD_STEPS: readonly WizardStep[] = [
  {
    id: 'property-type',
    title: 'Which of these best describes your place?',
    description: 'Pick the closest match. Guests filter by it.',
  },
  {
    id: 'room-type',
    title: 'What type of place will guests have?',
    description: 'Tell guests how much of the place is theirs.',
  },
  {
    id: 'location',
    title: 'Where is your place located?',
    description: 'Guests see the exact address only after they book.',
  },
  {
    id: 'basics',
    title: 'Share some basics about your place',
    description: 'You can add more details later.',
    section: 'basics',
  },
  {
    id: 'amenities',
    title: 'Tell guests what your place has to offer',
    description: 'You can add more amenities after you publish.',
    section: 'amenities',
  },
  {
    id: 'photos',
    title: 'Add some photos of your place',
    description: 'You need at least 5 photos to publish. The first one is the cover.',
  },
  {
    id: 'title',
    title: 'Now, give your place a title',
    description: 'Short titles work best. You can change it any time.',
    section: 'title',
  },
  {
    id: 'description',
    title: 'Create your description',
    description: 'Share what makes your place special, in at least 50 characters.',
    section: 'description',
  },
  {
    id: 'booking',
    title: 'Decide how guests book',
    description: 'Instant Book lets guests book right away. Otherwise they send a request.',
    section: 'booking',
  },
  {
    id: 'house-rules',
    title: 'Set your house rules',
    description: 'Guests agree to these before they book.',
    section: 'houseRules',
  },
  {
    id: 'price',
    title: 'Now, set your price',
    description: 'Friday and Saturday nights use the weekend price.',
    section: 'price',
  },
  {
    id: 'check-in',
    title: 'Check-in and checkout',
    description: 'The door code and Wi-Fi go only to booked guests, close to check-in.',
    section: 'checkIn',
  },
  {
    id: 'cancellation',
    title: 'Choose your cancellation policy',
    description: 'This decides what guests get back when they cancel.',
    section: 'cancellation',
  },
  {
    id: 'review',
    title: 'Review your listing',
    description: 'Here is what guests will see. Publish when everything is ready.',
  },
];

export function wizardStepIndex(id: WizardStepId): number {
  return WIZARD_STEPS.findIndex(step => step.id === id);
}

export function nextWizardStep(id: WizardStepId): WizardStepId | null {
  const index = wizardStepIndex(id);
  return index >= 0 && index < WIZARD_STEPS.length - 1 ? WIZARD_STEPS[index + 1].id : null;
}

export function previousWizardStep(id: WizardStepId): WizardStepId | null {
  const index = wizardStepIndex(id);
  return index > 0 ? WIZARD_STEPS[index - 1].id : null;
}

/**
 * Where a saved draft reopens. The first three steps happen before the draft
 * exists, so a draft never goes back to them on its own.
 */
export function resumeWizardStep(saved?: string | null): WizardStepId {
  const index = WIZARD_STEPS.findIndex(step => step.id === saved);
  if (index < wizardStepIndex('basics')) return 'basics';
  return WIZARD_STEPS[index].id;
}

// ─── listings page filter ──────────────────────────────────────────────────

export type ListingFilter = 'all' | 'listed' | 'drafts' | 'unlisted';

export const FILTERS: { key: ListingFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'listed', label: 'Listed' },
  { key: 'drafts', label: 'In progress' },
  { key: 'unlisted', label: 'Unlisted' },
];

/** Which listings each tab shows; the API filters one status at a time, so drafts are split here. */
export function matchesFilter(
  status: StayListingStatus | null | undefined,
  filter: ListingFilter,
): boolean {
  switch (filter) {
    case 'listed':
      return status === StayListingStatus.Listed;
    case 'drafts':
      return isDraftStatus(status);
    case 'unlisted':
      return status === StayListingStatus.Unlisted || status === StayListingStatus.Snoozed;
    default:
      return true;
  }
}
